import { createClient, EmailOtpType, MobileOtpType } from '@supabase/supabase-js'
import { Provider, UserIdentity } from '@supabase/auth-js/src/lib/types'

// Client-side Supabase: set in .env as NEXT_PUBLIC_* (see Next.js env docs).
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL ?? ''
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? ''
export const supabase = createClient(supabaseUrl, supabaseAnonKey)
export const use_supabase = process.env.NEXT_PUBLIC_USE_SUPABASE === 'true'

/**
     * 邮箱注册。
     *
     * @param {string} email - 邮箱
     * @returns {string} 返回值。
     */
export async function email_signUp(email: string,password: string) {
    console.log('email_signUp in = ',email,password)
    if (!(email && email.length)) {
      throw new Error("email can not be null")
    }
    if (!(password && password.length)) {
      throw new Error("password can not be null")
    }
    let { data, error } = await supabase.auth.signUp({
      email: email,
      password: password,
    })
    console.log('email_signUp = ',data,error)
    if (error) throw error
    if (data && data.user && !(data.user.identities && data.user.identities.length) && (data.user.user_metadata && !data.user.user_metadata.email)) {
      throw new Error('The email is already in use')
    }
    return data
}

/**
 * 邮箱登录。
 *
 * @param {string} email - 邮箱
 * @returns {string} 返回值。
 */
export async function email_login(email: string,password: string) {
    if (!(email && email.length)) {
        throw new Error("email can not be null")
    }
    if (!(password && password.length)) {
        throw new Error("password can not be null")
    }
    let { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
    })
    if (error) throw error
    let user =  data && data.session && data.session.user

    return data
}

 /**
     * 邮箱验证。
     *
     * @param {string} email - 邮箱
     * @param {string} otp_code - 验证码
     * @param {string} type - 'sms' | 'phone_change' | 'signup' | 'invite' | 'magiclink' | 'recovery' | 'email_change' | 'email' 
     * 重置密码 和 验证码登录 type 都等于 email
     * @returns {string} 返回值。
     */
export async function verifyOtp(email: string,otp_code: string,type: string) {
    if (!(email && email.length)) {
      throw new Error("email can not be null")
    }
    if (!(otp_code && otp_code.length)) {
      throw new Error("otp_code can not be null")
    }
    let { data, error } = await supabase.auth.verifyOtp({
      email: email,
      token: otp_code,
      type: type as EmailOtpType
    })
    if (error) throw error
    return data
}

export async function resend_verification_supabase(email: string) {
  let { data, error } = await supabase.auth.resend({
    email: email as string,
    type: 'signup',
  })
  if (error) throw error
  return data
}

/**
     * 用户是否登录。
     *
     * @returns {string} 返回值 为 当前登录用户的session。
     */
export async function login_user() {
    let { data, error } = await supabase.auth.getSession()
    if (error) {
      throw error
    }
    let user =  data && data.session && data.session.user
    return user
}

/**
     * 获取用户access_token。
     *
     * @returns {string} 返回值 为 当前登录用户的access_token。
     */
export async function get_access_token() {
    let { data, error } = await supabase.auth.getSession()
    if (error) {
      throw error
    }
    let access_token =  data && data.session && data.session.access_token
    if (!access_token) {
      return null
    }
    return access_token
  }


  export async function get_access_token_supabase() {
      let { data, error } = await supabase.auth.getSession()
      if (error) {
        throw error
      }
      let access_token =  data && data.session && data.session.access_token
      if (!access_token) {
        return null
      }
      return access_token
    }

/**
     * 重置密码。
     *
     * @param {string} email - 邮箱
     * @returns {string} 返回值。
     */
export async function resetPasswordForEmail(email: string, redirectTo?: string) {
    if (!(email && email.length)) {
      throw new Error("email can not be null")
    }
    const options = redirectTo ? { redirectTo } : undefined
    let { data, error } = await supabase.auth.resetPasswordForEmail(email, options)
    if (error) throw error
    return data
}

/**
     * 设置密码。
     *
     * @param {string} password - 密码
     * @returns {string} 返回值。
     */
export async function updatePassword(password: string) {
    if (!(password && password.length)) {
      throw new Error("password can not be null")
    }
    let { data, error } = await supabase.auth.updateUser({ password: password })
    if (error) throw error
    return data
}

export async function modify_user_name_supabase(first_name: string,last_name: string) {
  let { data, error } = await supabase.auth.updateUser({ data: { first_name: first_name, last_name: last_name } })
  if (error) throw error
  return data
}

/**
     * 三方登录。
     *
     * @param {string} provider - 第三方登录类型
     * @param {string} redirectTo - 重定向地址
     * @returns {string} 返回值。
     */
export async function third_party_login(provider: string,redirectTo: string) {
    if (!(provider && provider.length)) {
      throw new Error("provider can not be null")
    }
    let { data, error } = await supabase.auth.signInWithOAuth({
      provider: provider as Provider,
      options: {
        redirectTo: redirectTo
      }
    })
    if (error) throw error
    return data
}

/**
     * 邮箱绑定。
     *
     * @param {string} email - 邮箱
     * @param {string} password - 密码 可为空
     * @returns {string} 返回值。
     */
export async function bindEmail(email: string,password: string) {
    if (!(email && email.length)) {
      throw new Error("email can not be null")
    }
    let input_data: { email: string; password?: string } = { email: email }
    if (password && password.length) {
      input_data.password = password
    }
    const { data, error } = await supabase.auth.updateUser(input_data)
    if (error) throw error
    return data
}

/**
     * 绑定 第三方登录。
     *
     * @param {string} provider - 第三方登录类型
     * @param {string} redirectTo - 重定向地址
     * @returns {string} 返回值。
     */
export async function linkIdentity(provider: string,redirectTo: string) {
    let { data, error } = await supabase.auth.linkIdentity({
       provider: provider as Provider,
       options:{
        redirectTo:redirectTo
       }
    })
    if (error) throw error
    return data
}

export async function linkedIdentitys() {
  let { data, error } = await supabase.auth.getUser()
  if (error) throw error
  let user = data.user
  if (!user) {
    throw new Error('user is not exist')
  }
    return data
}

/**
     * 是否绑定第三方登录。
     *
     * @param {string} provider - 第三方登录类型
     * @returns {string} 返回值。
     */
export async function islinkIdentity(provider: string) {
    let { data, error } = await supabase.auth.getUser()
    if (error) throw error
    let user = data.user
    if (!user) {
      throw new Error('user is not exist')
    }
    let find = user?.identities?.find(e => e.provider == provider)
    return find
}

/**
     * 解绑第三方登录。
     *
     * @param {string} provider - 第三方登录类型
     * @returns {string} 返回值。
     */
export async function unlinkIdentity(provider: string) {
    if (!provider) {
      throw new Error("provider can not be null")
    }
    let identity = await islinkIdentity(provider)
    let { data, error } = await supabase.auth.unlinkIdentity(identity as UserIdentity)
    if (error) throw error
    return data
}

/**
     * logout
     *
     * @returns {string} 返回值,成功返回null。
     */
export async function logout() {
    let { error } = await supabase.auth.signOut()
    if (error) throw error
    return null
}

export async function logout_supabase() {
  let { error } = await supabase.auth.signOut()
  if (error) throw error
  return null
}